import React, {useMemo, useState} from "react";
import Icon from "@mui/material/Icon";
import {addDays, endOfDay, startOfDay, startOfToday, startOfYesterday} from "date-fns";
import {useTranslation} from "react-i18next";
import {DatePicker} from "@mui/x-date-pickers";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import MDButton from "materialDashboard/components/MDButton";
import DateGte from "../../../../components/inputs/DateGte";
import DateLte from "../../../../components/inputs/DateLte";
import {formatISODate, getSeasonFromDate} from "../../../../utils/date";

export const SINGLE_DAY_MODE = "singleDay";
export const RANGE_MODE = "range";

export const useTemporalState = () => {
    const [mode, setMode] = useState(SINGLE_DAY_MODE);
    const [date, setDate] = useState(startOfToday());
    const [dateGte, setDateGte] = useState(startOfYesterday());
    const [dateLte, setDateLte] = useState(endOfDay(new Date));

    const getDateRange = () => {
        if (mode === SINGLE_DAY_MODE) {
            return {
                dateGte: startOfDay(dateGte),
                dateLte: endOfDay(dateGte)
            }
        } else {
            return {
                dateGte,
                dateLte
            }
        }
    }

    const setNextDay = () => {
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        setDate(nextDay);
    }

    const setPreviousDay = () => {
        const previousDay = new Date(date);
        previousDay.setDate(previousDay.getDate() - 1);
        setDate(previousDay);
    }

    const filterDateGte = useMemo(() => {
        return mode === SINGLE_DAY_MODE ? startOfDay(date) : dateGte;
    }, [mode, date, dateGte]);

    const filterDateLte = useMemo(() => {
        return mode === SINGLE_DAY_MODE ? endOfDay(date) : dateLte;
    }, [mode, date, dateLte]);

    return {
        mode,
        date,
        dateGte,
        dateLte,
        setMode,
        setDateGte,
        setDateLte,
        setDate,
        filterDateGte,
        filterDateLte,
        setNextDay,
        setPreviousDay,
        getDateRange
    }
}


const TemporalFilters = ({temporalState, accordionStyle = {}}) => {
    const {t} = useTranslation(["webgis", "inputs"]);

    const today = new Date();
    const season = getSeasonFromDate(today);

    const handleSingleDay = () => {
        temporalState.setMode(SINGLE_DAY_MODE);
        temporalState.setDate(today);
    };

    const handle2Days = () => {
        temporalState.setMode(RANGE_MODE);
        temporalState.setDateLte(endOfDay(today));
        temporalState.setDateGte(startOfDay(addDays(today, -1)));
    };

    const handle7Days = () => {
        temporalState.setMode(RANGE_MODE);
        temporalState.setDateLte(endOfDay(today));
        temporalState.setDateGte(startOfDay(addDays(today, -7)));
    };

    const handleSeason = () => {
        temporalState.setMode(RANGE_MODE);
        temporalState.setDateGte(season.from);
        temporalState.setDateLte(season.to);
    };

    const isSingleDay = temporalState.mode === SINGLE_DAY_MODE;
    const is2Days = temporalState.mode === RANGE_MODE &&
        formatISODate(temporalState.dateLte) === formatISODate(endOfDay(today)) &&
        formatISODate(temporalState.dateGte) === formatISODate(startOfDay(addDays(today, -1)));
    const is7Days = temporalState.mode === RANGE_MODE &&
        formatISODate(temporalState.dateLte) === formatISODate(endOfDay(today)) &&
        formatISODate(temporalState.dateGte) === formatISODate(startOfDay(addDays(today, -7)));
    const isSeason = temporalState.mode === RANGE_MODE &&
        formatISODate(temporalState.dateGte) === formatISODate(season.from) &&
        formatISODate(temporalState.dateLte) === formatISODate(season.to);

    const btnStyle = {px: 1, minWidth: 0, flex: 1};

    return <Stack spacing={3}>
        <Stack direction="row" spacing={0.5} justifyContent="space-between">
            <MDButton size="small" variant={isSingleDay ? "contained" : "outlined"} color="info"
                      onClick={handleSingleDay} sx={btnStyle}>{t('filtersPanel.temporalFilters.1dd')}</MDButton>
            <MDButton size="small" variant={is2Days ? "contained" : "outlined"} color="info"
                      onClick={handle2Days} sx={btnStyle}>{t('filtersPanel.temporalFilters.2dd')}</MDButton>
            <MDButton size="small" variant={is7Days ? "contained" : "outlined"} color="info"
                      onClick={handle7Days} sx={btnStyle}>{t('filtersPanel.temporalFilters.7dd')}</MDButton>
            <MDButton size="small" variant={isSeason ? "contained" : "outlined"} color="info"
                      onClick={handleSeason} sx={btnStyle}>
                {t('filtersPanel.temporalFilters.season')}
            </MDButton>
        </Stack>

        {
            temporalState.mode === SINGLE_DAY_MODE &&
            <Stack direction="row" spacing={1} alignItems={"center"} justifyContent="center">
                <IconButton onClick={temporalState.setPreviousDay} size="small">
                    <Icon>navigate_before</Icon>
                </IconButton>
                <DatePicker value={temporalState.date}
                            onChange={(newValue) => {
                                if (newValue && !isNaN(newValue.getTime())) {
                                    temporalState.setDate(newValue);
                                }
                            }}/>
                <IconButton onClick={temporalState.setNextDay} size="small">
                    <Icon>navigate_next</Icon>
                </IconButton>
            </Stack>
        }
        {
            temporalState.mode === RANGE_MODE &&
            <Stack spacing={1} flexWrap="noWrap" direction="row" justifyContent="center">
                <DateGte dateGte={temporalState.dateGte}
                         setDateGte={temporalState.setDateGte}
                         maxDate={temporalState.dateLte}
                         size="small"
                />
                <DateLte dateLte={temporalState.dateLte}
                         setDateLte={temporalState.setDateLte}
                         mindate={temporalState.dateGte}
                         size="small"
                />
            </Stack>
        }
    </Stack>
}


export default TemporalFilters;